/**
 * Vanilla pitch video player — same markup + behaviour as public/test-video.html.
 * Styles: public/test-video-player.css (injected into shadow root by PitchVideoPlayer).
 */

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];
const FS_CLASS = "pitch-video-player--fullscreen";

const PLAY_ICON =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M10 8.2v7.6c0 .6.7 1 1.2.6l6-3.8c.5-.3.5-1 0-1.3l-6-3.8c-.5-.4-1.2 0-1.2.7z" fill="currentColor"/></svg>';
const PAUSE_ICON =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6.5v11" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/><path d="M15 6.5v11" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>';
const VOLUME_ON =
  '<svg viewBox="0 0 24 24" fill="none"><path d="M10 9H6a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h4l4 3V6z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M16 9.5c1.2 1 1.2 4 0 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
const VOLUME_OFF =
  '<svg viewBox="0 0 24 24" fill="none"><path d="M10 9H6a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h4l4 3V6z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';

const PLAYER_MARKUP = `
<div class="pitch-video-player__frame">
  <div class="pitch-video-player__media" data-player-media>
    <video
      class="pitch-video-player__video"
      playsinline
      preload="metadata"
      disablepictureinpicture
    >
      Your browser does not support embedded video.
    </video>

    <button
      type="button"
      class="pitch-video-player__center-play is-hidden"
      data-player-center-play
      aria-label="Play pitch video"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M10 8.2v7.6c0 .6.7 1 1.2.6l6-3.8c.5-.3.5-1 0-1.3l-6-3.8c-.5-.4-1.2 0-1.2.7z"
          fill="currentColor"
        />
      </svg>
    </button>

    <div class="pitch-video-player__media-chrome is-hidden" data-player-chrome>
      <div class="pitch-video-player__time" data-player-time aria-live="polite">0:00 / 0:00</div>
      <div class="pitch-video-player__timeline-wrap" data-player-timeline-wrap>
        <input
          class="pitch-video-player__timeline"
          data-player-timeline
          type="range"
          min="0"
          max="0"
          step="0.1"
          value="0"
          aria-label="Seek"
        />
      </div>
    </div>

    <div class="pitch-video-player__controls" data-player-controls role="group" aria-label="Pitch video controls">
      <button
        type="button"
        class="pitch-video-player__control-btn pitch-video-player__control-btn--play"
        data-player-play-btn
        aria-label="Play"
        title="Play"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M10 8.2v7.6c0 .6.7 1 1.2.6l6-3.8c.5-.3.5-1 0-1.3l-6-3.8c-.5-.4-1.2 0-1.2.7z"
            fill="currentColor"
          />
        </svg>
      </button>

      <div class="pitch-video-player__speed" role="group" aria-label="Playback speed" data-player-speed>
        <button type="button" class="pitch-video-player__speed-btn is-active" data-rate="1">1x</button>
        <button type="button" class="pitch-video-player__speed-btn" data-rate="1.25">1.25x</button>
        <button type="button" class="pitch-video-player__speed-btn" data-rate="1.5">1.5x</button>
        <button type="button" class="pitch-video-player__speed-btn" data-rate="2">2x</button>
      </div>

      <label class="pitch-video-player__volume">
        <span class="pitch-video-player__volume-icon" aria-hidden="true" data-player-volume-icon>
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M10 9H6a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h4l4 3V6z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />
            <path
              d="M16 9.5c1.2 1 1.2 4 0 5"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <input
          class="pitch-video-player__volume-range"
          data-player-volume
          type="range"
          min="0"
          max="1"
          step="0.05"
          value="1"
          aria-label="Volume"
        />
      </label>

      <button
        type="button"
        class="pitch-video-player__control-btn pitch-video-player__control-btn--fullscreen"
        data-player-fs-btn
        aria-label="Fullscreen"
        title="Fullscreen"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M9 3H5a2 2 0 0 0-2 2v4M15 3h4a2 2 0 0 1 2 2v4M9 21H5a2 2 0 0 1-2-2v-4M15 21h4a2 2 0 0 0 2-2v-4"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
  </div>
</div>
`;

function getFullscreenElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function requestFullscreen(el) {
  const fn = el.requestFullscreen || el.webkitRequestFullscreen;
  return fn ? Promise.resolve(fn.call(el)) : Promise.reject();
}

function exitFullscreen() {
  const fn = document.exitFullscreen || document.webkitExitFullscreen;
  return fn ? Promise.resolve(fn.call(document)) : Promise.reject();
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatDurationLabel(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "--:--";
  return formatTime(seconds);
}

function setHidden(el, hidden) {
  el.classList.toggle("is-hidden", hidden);
}

/** @returns {() => void} cleanup */
export function mountPitchVideoPlayer(
  root,
  { src, ariaLabel = "Pitch video", preload = "metadata", knownDurationSec = 0, autoplay = false } = {}
) {
  root.innerHTML = PLAYER_MARKUP;

  const media = root.querySelector("[data-player-media]");
  const video = root.querySelector(".pitch-video-player__video");
  const centerPlay = root.querySelector("[data-player-center-play]");
  const chrome = root.querySelector("[data-player-chrome]");
  const timeEl = root.querySelector("[data-player-time]");
  const timeline = root.querySelector("[data-player-timeline]");
  const playBtn = root.querySelector("[data-player-play-btn]");
  const fsBtn = root.querySelector("[data-player-fs-btn]");
  const volumeInput = root.querySelector("[data-player-volume]");
  const volumeIcon = root.querySelector("[data-player-volume-icon]");
  const speedGroup = root.querySelector("[data-player-speed]");

  video.setAttribute("aria-label", ariaLabel);
  video.setAttribute("preload", preload);

  let isPlaying = false;
  let isFullscreen = false;
  let playbackRate = 1;
  let durationProbeAttempted = false;
  let autoplayAttempted = false;
  const fallbackDurationSec =
    Number.isFinite(Number(knownDurationSec)) && Number(knownDurationSec) > 0
      ? Number(knownDurationSec)
      : 0;

  function layoutMediaHeight() {
    if (isFullscreen) {
      media.style.height = `${root.clientHeight}px`;
    } else {
      media.style.removeProperty("height");
    }
  }

  function getPlayableDuration() {
    if (Number.isFinite(video.duration) && video.duration > 0) return video.duration;
    if (video.seekable && video.seekable.length > 0) {
      const end = video.seekable.end(video.seekable.length - 1);
      if (Number.isFinite(end) && end > 0) return end;
    }
    if (fallbackDurationSec > 0) return fallbackDurationSec;
    return 0;
  }

  function probeDurationIfNeeded() {
    if (durationProbeAttempted || getPlayableDuration() > 0) return;
    durationProbeAttempted = true;
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      if (getPlayableDuration() > 0) {
        video.currentTime = 0;
        updateProgress();
      }
    };
    video.addEventListener("seeked", onSeeked);
    try {
      video.currentTime = 1e10;
    } catch {
      /* ignore */
    }
  }

  function updateProgress() {
    const duration = getPlayableDuration();
    const current = video.currentTime || 0;
    const timelineMax = duration > 0 ? duration : Math.max(current, 0);
    const pct =
      timelineMax > 0 ? `${(Math.min(current, timelineMax) / timelineMax) * 100}%` : "0%";
    timeline.style.setProperty("--progress", pct);
    timeline.max = String(timelineMax || 0);
    timeline.value = String(Math.min(current, timelineMax || 0));
    timeEl.textContent = `${formatTime(current)} / ${formatDurationLabel(duration)}`;
  }

  function updateVolumeUi() {
    const v = video.muted ? 0 : Math.min(1, Math.max(0, video.volume));
    volumeInput.value = String(v);
    volumeInput.style.setProperty("--volume-progress", `${v * 100}%`);
    volumeIcon.innerHTML = v > 0 ? VOLUME_ON : VOLUME_OFF;
  }

  function updatePlayUi() {
    playBtn.innerHTML = isPlaying ? PAUSE_ICON : PLAY_ICON;
    playBtn.setAttribute("aria-label", isPlaying ? "Pause" : "Play");
    playBtn.setAttribute("title", isPlaying ? "Pause" : "Play");
    setHidden(centerPlay, isPlaying || isFullscreen);
    setHidden(chrome, !isPlaying);
  }

  function syncFullscreen() {
    isFullscreen = getFullscreenElement() === root;
    root.classList.toggle(FS_CLASS, isFullscreen);
    fsBtn.setAttribute("aria-label", isFullscreen ? "Exit fullscreen" : "Fullscreen");
    fsBtn.setAttribute("title", isFullscreen ? "Exit fullscreen" : "Fullscreen");
    requestAnimationFrame(() => {
      layoutMediaHeight();
      updatePlayUi();
    });
  }

  function tryAutoplay() {
    if (!autoplay || autoplayAttempted) return;
    autoplayAttempted = true;
    video.play().catch(() => {});
  }

  function togglePlay() {
    if (video.paused || video.ended) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  function toggleFullscreen() {
    if (getFullscreenElement() === root) {
      exitFullscreen().catch(() => {});
      return;
    }
    requestFullscreen(root).then(syncFullscreen).catch(() => {});
  }

  function onPlay() {
    isPlaying = true;
    updatePlayUi();
    updateProgress();
  }

  function onPause() {
    isPlaying = false;
    updatePlayUi();
  }

  function onEnded() {
    isPlaying = false;
    updatePlayUi();
  }

  function onTimeUpdate() {
    updateProgress();
  }

  function onLoadedMetadata() {
    updateProgress();
    probeDurationIfNeeded();
  }

  function onCanPlay() {
    probeDurationIfNeeded();
    updateProgress();
    tryAutoplay();
  }

  function onVolumeChange() {
    updateVolumeUi();
  }

  function onVideoClick(e) {
    e.preventDefault();
    togglePlay();
  }

  function onVideoDblClick(e) {
    e.preventDefault();
    toggleFullscreen();
  }

  function onCenterPlayClick(e) {
    e.preventDefault();
    e.stopPropagation();
    togglePlay();
  }

  function onTimelineInput() {
    const seekEnd = getPlayableDuration();
    if (seekEnd <= 0) return;
    video.currentTime = Math.min(seekEnd, Math.max(0, Number(timeline.value)));
    updateProgress();
  }

  function onVolumeInput() {
    const next = Math.min(1, Math.max(0, Number(volumeInput.value)));
    video.volume = next;
    video.muted = next === 0;
    updateVolumeUi();
  }

  function onSpeedClick(e) {
    const btn = e.target.closest("[data-rate]");
    if (!btn) return;
    const rate = Number(btn.getAttribute("data-rate"));
    if (!PLAYBACK_RATES.includes(rate)) return;
    playbackRate = rate;
    video.playbackRate = rate;
    speedGroup.querySelectorAll(".pitch-video-player__speed-btn").forEach((el) => {
      el.classList.toggle("is-active", Number(el.getAttribute("data-rate")) === rate);
      el.setAttribute("aria-pressed", el.classList.contains("is-active") ? "true" : "false");
    });
  }

  function onResize() {
    layoutMediaHeight();
  }

  video.addEventListener("play", onPlay);
  video.addEventListener("pause", onPause);
  video.addEventListener("ended", onEnded);
  video.addEventListener("timeupdate", onTimeUpdate);
  video.addEventListener("progress", updateProgress);
  video.addEventListener("durationchange", updateProgress);
  video.addEventListener("loadedmetadata", onLoadedMetadata);
  video.addEventListener("canplay", onCanPlay);
  video.addEventListener("volumechange", onVolumeChange);
  video.addEventListener("click", onVideoClick);
  video.addEventListener("dblclick", onVideoDblClick);
  centerPlay.addEventListener("click", onCenterPlayClick);
  playBtn.addEventListener("click", togglePlay);
  fsBtn.addEventListener("click", toggleFullscreen);
  timeline.addEventListener("input", onTimelineInput);
  volumeInput.addEventListener("input", onVolumeInput);
  speedGroup.addEventListener("click", onSpeedClick);
  document.addEventListener("fullscreenchange", syncFullscreen);
  document.addEventListener("webkitfullscreenchange", syncFullscreen);
  window.addEventListener("resize", onResize);

  video.volume = 1;
  video.playbackRate = playbackRate;
  video.src = src;
  video.load();
  updateVolumeUi();
  updatePlayUi();
  updateProgress();
  if (autoplay && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    tryAutoplay();
  }

  return function unmountPitchVideoPlayer() {
    if (getFullscreenElement() === root) {
      exitFullscreen().catch(() => {});
    }
    root.classList.remove(FS_CLASS);
    video.pause();
    video.removeAttribute("src");
    video.load();
    video.removeEventListener("play", onPlay);
    video.removeEventListener("pause", onPause);
    video.removeEventListener("ended", onEnded);
    video.removeEventListener("timeupdate", onTimeUpdate);
    video.removeEventListener("progress", updateProgress);
    video.removeEventListener("durationchange", updateProgress);
    video.removeEventListener("loadedmetadata", onLoadedMetadata);
    video.removeEventListener("canplay", onCanPlay);
    video.removeEventListener("volumechange", onVolumeChange);
    video.removeEventListener("click", onVideoClick);
    video.removeEventListener("dblclick", onVideoDblClick);
    centerPlay.removeEventListener("click", onCenterPlayClick);
    playBtn.removeEventListener("click", togglePlay);
    fsBtn.removeEventListener("click", toggleFullscreen);
    timeline.removeEventListener("input", onTimelineInput);
    volumeInput.removeEventListener("input", onVolumeInput);
    speedGroup.removeEventListener("click", onSpeedClick);
    document.removeEventListener("fullscreenchange", syncFullscreen);
    document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    window.removeEventListener("resize", onResize);
    media.style.removeProperty("height");
    root.innerHTML = "";
  };
}
